import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './LoginModal.css';

const KNOWN_ERROR_CODES = ['state', 'kakao', 'denied', 'token', 'invalid'] as const;
type OAuthErrorCode = (typeof KNOWN_ERROR_CODES)[number];

function isKnownError(code: string | null): code is OAuthErrorCode {
  return !!code && (KNOWN_ERROR_CODES as readonly string[]).includes(code);
}

function buildRedirectTo(pathname: string, search: string): string {
  // Drop login/error params so the user lands on a clean URL after auth.
  const params = new URLSearchParams(search);
  params.delete('login');
  params.delete('error');
  const qs = params.toString();
  return pathname + (qs ? `?${qs}` : '');
}

export function LoginModal() {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const navigate = useNavigate();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isOpen = searchParams.get('login') === '1';
  const errorCode = searchParams.get('error');

  const close = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('login');
    params.delete('error');
    const qs = params.toString();
    navigate(location.pathname + (qs ? `?${qs}` : '') + location.hash, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    closeButtonRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const redirectTo = buildRedirectTo(location.pathname, location.search);
  const encoded = encodeURIComponent(redirectTo);

  const startKakao = () => {
    window.location.href = `/api/auth/kakao?redirect_to=${encoded}`;
  };
  const startGoogle = () => {
    window.location.href = `/api/auth/google?redirect_to=${encoded}`;
  };

  const errorMessage = isKnownError(errorCode) ? t(`errors.${errorCode}`) : null;

  return (
    <div
      className="login-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="login-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="login-modal-close"
          onClick={close}
          aria-label={t('modal.close')}
        >
          ×
        </button>
        <h2 id="login-modal-title" className="login-modal-title">
          {t('modal.title')}
        </h2>
        {errorMessage && (
          <div className="login-modal-error" role="alert">
            {errorMessage}
          </div>
        )}
        <div className="login-modal-buttons">
          <button
            type="button"
            className="login-btn login-btn-kakao"
            onClick={startKakao}
            aria-label={t('kakao')}
          >
            <span className="login-btn-icon" aria-hidden="true">K</span>
            <span>{t('kakao')}</span>
          </button>
          <button
            type="button"
            className="login-btn login-btn-google"
            onClick={startGoogle}
            aria-label={t('google')}
          >
            <span className="login-btn-icon" aria-hidden="true">G</span>
            <span>{t('google')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
